/**
 * Carrying an Exercise Settings change from the settings window onto the stored program, for
 * the Training Overview's exercise menu (#5456, phase 3).
 *
 * The settings window edits a COPY of the whole plan and fitness state, taken when the menu was
 * opened, and hands both back. Writing those copies over the stored documents would undo
 * everything written in between. The same program entry and the same per-exercise stats also
 * hold what TRAINING writes: target weights, last-session figures, the strength estimate.
 *
 * One example. Anna (a coach) opens Exercise Settings for BORI's Bench Press at 18:00 and changes
 * the heavy rep target from 6 to 5. While the window is open BORI finishes a set on his phone and
 * the app raises Bench Press's target weight from 82.5 to 85 kg. Anna saves at 18:05. Only the rep
 * target is carried over: the stored plan ends with the rep target 5 AND the target weight 85 kg.
 *
 * So the settings are summarised before the window opens and again from what it hands back, and
 * only the parts whose summaries differ are written onto the fresh documents.
 *
 * Pure: no Angular and no database (`training-overview-actions.test.ts`). The service that opens
 * the menu and saves is `training-overview-actions.service.ts`.
 */

import { deepClone, deepCompareEQ } from '@cf-shared/deep-helpers';
import { IntensityLevel, pathAtLevel, Plan_ExercisePath, Plan_SubSlot } from '@cf-shared/data-model/fitness-plan';
import { setPlanSetCount } from '@cf-shared/data-model/plan-set-count';
import { ExerciseStat, FitnessState, MyoRepsMode } from '@cf-shared/data-model/user-state';
import { AdvancedMethodsRMTesting } from '@cf-shared/enums';
import { clearAutoregulationFields } from 'pages/workout-detail/autoregulation-resolve';

/** a number per intensity level; a level that is absent has no value */
export type NumberByLevel = { [level: number]: number | undefined };

/**
 * Everything the Exercise Settings window can change for one exercise in one program entry. This is
 * the one list of what a save carries over: `isSameExerciseSettings` compares the whole summary, and
 * `SETTINGS_WRITERS` must name a writer for every field, so a new field cannot be forgotten in either.
 *
 * Each per-level map holds only the levels that have a value, so "absent" has one representation and
 * a plain deep comparison is enough.
 */
export interface ExerciseSettingsSummary {
  /** the program entry's base set count */
  setCount: number | undefined;
  /** the set adjustment the app stores per intensity; a level with no path, or an adjustment of 0, is absent */
  volumeAdjustments: NumberByLevel;
  /** the rep target per intensity; a level with no path or no target is absent */
  repTargets: NumberByLevel;
  preferMyoReps: MyoRepsMode | undefined;
  preferRMTesting: AdvancedMethodsRMTesting | undefined;
  /** the rep target the person asked for, per intensity */
  preferRepTargetByIntensity: NumberByLevel;
}

/** what travels through the settings window and comes back with its result */
export interface ExerciseSettingsContext {
  subSlotId: string;
  exerciseId: string;
  /** the settings as they were when the window was opened */
  before: ExerciseSettingsSummary;
  /**
   * #6428: the program entry and the two stats the coach's parts can change, as the window opened
   * them. Present only when the window was opened with `coachEditable`; carried in the page params
   * so the save does not depend on memory.
   */
  coachOriginal?: {
    subSlot: Plan_SubSlot;
    stat: ExerciseStat | undefined;
    setAsideExerciseId: string | undefined;
    setAsideStat: ExerciseStat | undefined;
  };
}

/** a finite number, or undefined for anything else (a damaged or old-shaped document) */
function finiteOrUndefined(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * The settings of one exercise in one program entry. Tolerates a missing stats entry, a missing
 * program entry and an entry with no paths: each simply has no values.
 */
export function summarizeExerciseSettings(subSlot: Plan_SubSlot | undefined, stat: ExerciseStat | undefined): ExerciseSettingsSummary {
  const volumeAdjustments: NumberByLevel = {};
  const repTargets: NumberByLevel = {};
  for (const path of subSlot?.exercisePaths ?? []) {
    if (!path || path.intensityLevel === undefined) continue;
    // if a damaged entry has two paths at one level, the later one wins, so an absent value deletes the key
    const adjustment = finiteOrUndefined(path.volumeAdjustment) ?? 0;
    if (adjustment !== 0) {
      volumeAdjustments[path.intensityLevel] = adjustment;
    } else {
      delete volumeAdjustments[path.intensityLevel];
    }
    const reps = finiteOrUndefined(path.target_reps);
    if (reps !== undefined) {
      repTargets[path.intensityLevel] = reps;
    } else {
      delete repTargets[path.intensityLevel];
    }
  }
  const preferRepTargetByIntensity: NumberByLevel = {};
  for (const [level, reps] of Object.entries(stat?.preferRepTargetByIntensity ?? {})) {
    const value = finiteOrUndefined(reps);
    if (value !== undefined) {
      preferRepTargetByIntensity[Number(level)] = value;
    }
  }
  return {
    setCount: finiteOrUndefined(subSlot?.defaultVolume_sets),
    volumeAdjustments,
    repTargets,
    preferMyoReps: stat?.preferMyoReps,
    preferRMTesting: stat?.preferRMTesting,
    preferRepTargetByIntensity,
  };
}

/** the levels at which two per-level maps differ; a missing level has no value */
function differingLevels(before: NumberByLevel, after: NumberByLevel): number[] {
  const levels = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})].map(Number));
  return [...levels].filter(level => before?.[level] !== after?.[level]);
}

/** true when the window changed nothing that `applyExerciseSettingsChange` would carry over */
export function isSameExerciseSettings(before: ExerciseSettingsSummary, after: ExerciseSettingsSummary): boolean {
  return deepCompareEQ(before, after);
}

/** what a writer needs to carry one changed field onto the fresh documents */
interface SettingsWrite {
  freshSubSlot: Plan_SubSlot;
  /** the fresh stats entry for the exercise, created on first use */
  stat: () => ExerciseStat;
  before: ExerciseSettingsSummary;
  after: ExerciseSettingsSummary;
  resultSubSlot: Plan_SubSlot | undefined;
}

type SettingsWriter = (write: SettingsWrite) => void;

/** the window writes the count and clears every adjustment together, so the two fields share this one writer */
const writeSetCount: SettingsWriter = ({ freshSubSlot, after }) => {
  if (after.setCount !== undefined) {
    setPlanSetCount(freshSubSlot, after.setCount);
  }
};

/** writes each changed rep target onto the fresh path, or copies the window's path for an intensity the fresh entry lacks */
const writeRepTargets: SettingsWriter = ({ freshSubSlot, before, after, resultSubSlot }) => {
  // for each intensity whose rep target was changed
  for (const level of differingLevels(before.repTargets, after.repTargets)) {
    freshSubSlot.exercisePaths ??= [];
    const freshPath = freshSubSlot.exercisePaths.find(path => path?.intensityLevel === level);
    if (freshPath) {
      if (after.repTargets[level] === undefined) {
        delete freshPath.target_reps;
      } else {
        freshPath.target_reps = after.repTargets[level];
      }
      continue;
    }
    // the window added a path for an intensity that has none yet: take the path it made
    const resultPath = resultSubSlot?.exercisePaths?.find(path => path?.intensityLevel === level);
    if (resultPath) {
      freshSubSlot.exercisePaths.push(deepClone(resultPath));
    }
  }
};

/** writes each changed rep-target preference onto the fresh stats entry, deleting one the person cleared */
const writePreferredRepTargets: SettingsWriter = ({ stat, before, after }) => {
  for (const level of differingLevels(before.preferRepTargetByIntensity, after.preferRepTargetByIntensity)) {
    const fresh = stat();
    fresh.preferRepTargetByIntensity ??= {};
    const reps = after.preferRepTargetByIntensity[level];
    if (reps === undefined) {
      delete fresh.preferRepTargetByIntensity[level as IntensityLevel];
    } else {
      fresh.preferRepTargetByIntensity[level as IntensityLevel] = reps;
    }
  }
};

/**
 * One writer per summary field. The type requires an entry for every field, so a field added to the
 * summary without a way to save it fails to compile instead of being dropped on save. The order is
 * the order the writers run in.
 */
const SETTINGS_WRITERS: { [K in keyof ExerciseSettingsSummary]: SettingsWriter } = {
  setCount: writeSetCount,
  volumeAdjustments: writeSetCount,
  repTargets: writeRepTargets,
  // the preferences live in the per-exercise stats
  preferMyoReps: ({ stat, after }) => { stat().preferMyoReps = after.preferMyoReps; },
  preferRMTesting: ({ stat, after }) => { stat().preferRMTesting = after.preferRMTesting; },
  preferRepTargetByIntensity: writePreferredRepTargets,
};

/**
 * Writes onto the FRESH program entry and stats exactly the parts of the settings that differ
 * between `before` and `after`, and nothing else. Nothing is deleted from the fresh documents
 * except a rep-target preference the person cleared. The stats entry is created only when a
 * preference changed.
 *
 * @param freshSubSlot the program entry as stored now
 * @param freshStats the per-exercise stats as stored now; an entry is created when one is needed
 * @param resultSubSlot the program entry the settings window handed back; a path it added for an
 *   intensity the fresh entry does not have yet is copied from here
 */
export function applyExerciseSettingsChange(
  freshSubSlot: Plan_SubSlot,
  freshStats: FitnessState['exerciseStats'],
  exerciseId: string,
  before: ExerciseSettingsSummary,
  after: ExerciseSettingsSummary,
  resultSubSlot: Plan_SubSlot | undefined,
): void {
  // for each field that changed, its writer; a writer two fields share runs once
  const writers = new Set<SettingsWriter>();
  for (const field of Object.keys(SETTINGS_WRITERS) as (keyof ExerciseSettingsSummary)[]) {
    if (!deepCompareEQ({ value: before[field] }, { value: after[field] })) {
      writers.add(SETTINGS_WRITERS[field]);
    }
  }
  const stat = () => freshStats[exerciseId] ?? (freshStats[exerciseId] = {} as ExerciseStat);
  for (const writer of writers) {
    writer({ freshSubSlot, stat, before, after, resultSubSlot });
  }
}

/** what `carryCoachExerciseChange` did */
export enum CoachCarryOutcome {
  /** the window changed none of the coach-only parts; today's merge alone applies */
  NothingToCarry = 'NothingToCarry',
  Carried = 'Carried',
  /** the client trained while the window was open: nothing may be saved */
  ChangedMeanwhile = 'ChangedMeanwhile',
}

/** the coach-only parts the window can change, as it opened and as the coach left them (#6428) */
export interface CoachExerciseEdit {
  /** the sub-slot as the window opened it */
  beforeSubSlot: Plan_SubSlot;
  /** the sub-slot the window handed back */
  editedSubSlot: Plan_SubSlot;
  /** true when a scheme switch (or Calculate Weights) built the paths in the window */
  pathsRebuiltBySwitch: boolean;
  /** true when the person changed the Sets count (the page's own record; the stored base cannot tell) */
  volumeChanged: boolean;
  /** the set-aside exercise's id and its stat, as opened and as handed back */
  setAsideExerciseId: string | undefined;
  beforeSetAsideStat: ExerciseStat | undefined;
  editedSetAsideStat: ExerciseStat | undefined;
  /** the exercise in the slot and its stat, as opened and as handed back: in the rare case it is itself set aside, Bring Back Now clears it */
  exerciseId: string;
  beforeStat: ExerciseStat | undefined;
  editedStat: ExerciseStat | undefined;
}

/** the fields today's merge carries on its own; any OTHER difference means training wrote meanwhile */
function withoutMergeCarriedFields(subSlot: Plan_SubSlot | undefined): Plan_SubSlot | undefined {
  if (!subSlot) return subSlot;
  const copy = deepClone(subSlot);
  delete copy.defaultVolume_sets;
  for (const path of copy.exercisePaths ?? []) {
    if (!path) continue;
    delete path.target_reps;
    delete path.volumeAdjustment;
  }
  return copy;
}

/** assign a number, or delete the field when the edited side has none (the shape `target_reps` uses above) */
function assignOrDelete(path: Plan_ExercisePath, field: 'target_weight_kg' | 'min_reps', value: number | undefined) {
  if (value === undefined) {
    delete path[field];
  } else {
    path[field] = value;
  }
}

/**
 * Carries the coach-only parts of an Exercise Settings save (#6428) onto the FRESH program entry.
 * Runs BEFORE `applyExerciseSettingsChange` (see `applyExerciseSettingsSave`): that merge adds
 * the paths of new days, and comparing after it would read every switch as training done
 * meanwhile. Runs only for a save the page marked as a coach edit, so an ordinary user's save never
 * reaches it.
 *
 * - A scheme switch replaces the fresh paths with the window's, with the day that comes next, any
 *   pending rebuild, and the answered plateau question.
 * - Otherwise only the days whose Next Weight or Next Minimum Reps the coach changed are copied. A
 *   cleared Next Minimum Reps deletes the field: `1` and absent mean different things (`1` is the
 *   grace after a progression, absent means never trained).
 * - A Volume change answers the volume question that was open when the window opened, and only
 *   that one: a question training raised meanwhile was never shown to the coach.
 * - Bring Back Now clears the set-aside exercise's quarantine on the fresh stats (or, in the rare
 *   case the exercise in the slot is itself set aside, that one's).
 *
 * Refuses (`ChangedMeanwhile`) when the window changed a coach-only part AND the fresh entry
 * differs from the one the window opened in anything but what today's merge carries: the client
 * trained meanwhile, and the coach's weights and the deload rule were worked out from history that
 * is now out of date. Example: the coach lowers the next weight 77.5 -> 72.5 kg at 18:00, the
 * client's set raises the stored weight to 80 kg at 18:02, the coach saves at 18:05: nothing is
 * saved, and reopening shows 80 kg. A rep-target-only edit never refuses.
 */
export function carryCoachExerciseChange(
  freshSubSlot: Plan_SubSlot,
  freshStats: FitnessState['exerciseStats'],
  edit: CoachExerciseEdit,
): CoachCarryOutcome {
  const { beforeSubSlot, editedSubSlot } = edit;
  const levels = (editedSubSlot.exercisePaths ?? []).filter(path => !!path).map(path => path.intensityLevel);
  const changedLevels = edit.pathsRebuiltBySwitch ? [] : levels.filter(level =>
    pathAtLevel(beforeSubSlot, level)?.target_weight_kg !== pathAtLevel(editedSubSlot, level)?.target_weight_kg
    || pathAtLevel(beforeSubSlot, level)?.min_reps !== pathAtLevel(editedSubSlot, level)?.min_reps);
  const isBroughtBack = (before: ExerciseStat | undefined, edited: ExerciseStat | undefined) =>
    ExerciseStat.hasQuarantineRecorded(before) && !ExerciseStat.hasQuarantineRecorded(edited);
  const setAsideBroughtBack = !!edit.setAsideExerciseId && isBroughtBack(edit.beforeSetAsideStat, edit.editedSetAsideStat);
  const currentBroughtBack = isBroughtBack(edit.beforeStat, edit.editedStat);
  const broughtBack = setAsideBroughtBack || currentBroughtBack;
  const volumeChanged = edit.volumeChanged;

  if (!edit.pathsRebuiltBySwitch && changedLevels.length === 0 && !broughtBack && !volumeChanged) {
    return CoachCarryOutcome.NothingToCarry;
  }
  // a Volume change alone is today's merge plus answering an open question, which needs no fresh history
  const needsUnchangedEntry = edit.pathsRebuiltBySwitch || changedLevels.length > 0 || broughtBack;
  if (needsUnchangedEntry && !deepCompareEQ(withoutMergeCarriedFields(freshSubSlot), withoutMergeCarriedFields(beforeSubSlot))) {
    return CoachCarryOutcome.ChangedMeanwhile;
  }

  if (edit.pathsRebuiltBySwitch) {
    freshSubSlot.exercisePaths = deepClone(editedSubSlot.exercisePaths ?? []);
    if (editedSubSlot.nextWorkoutIntensity !== undefined) {
      freshSubSlot.nextWorkoutIntensity = editedSubSlot.nextWorkoutIntensity;
    } else {
      delete freshSubSlot.nextWorkoutIntensity;
    }
    if (editedSubSlot.rebuildPathsForIntensity !== undefined) {
      freshSubSlot.rebuildPathsForIntensity = editedSubSlot.rebuildPathsForIntensity;
    } else {
      delete freshSubSlot.rebuildPathsForIntensity;
    }
  } else {
    for (const level of changedLevels) {
      const freshPath = pathAtLevel(freshSubSlot, level);
      const editedPath = pathAtLevel(editedSubSlot, level);
      if (!freshPath || !editedPath) continue;
      assignOrDelete(freshPath, 'target_weight_kg', editedPath.target_weight_kg);
      assignOrDelete(freshPath, 'min_reps', editedPath.min_reps);
    }
  }
  if (beforeSubSlot.plateauWorkoutLog_id && !editedSubSlot.plateauWorkoutLog_id) {
    delete freshSubSlot.plateauWorkoutLog_id;
  }

  // the volume question open when the window opened is answered; one raised since is left alone
  if (volumeChanged && !edit.pathsRebuiltBySwitch) {
    for (const freshPath of freshSubSlot.exercisePaths ?? []) {
      const openedWith = pathAtLevel(beforeSubSlot, freshPath?.intensityLevel);
      if (freshPath && openedWith?.autoregulationWorkoutLog_id
        && openedWith.autoregulationWorkoutLog_id === freshPath.autoregulationWorkoutLog_id) {
        clearAutoregulationFields(freshPath);
      }
    }
  }

  if (setAsideBroughtBack && freshStats[edit.setAsideExerciseId]) {
    ExerciseStat.unmarkQuarantined(freshStats[edit.setAsideExerciseId]);
  }
  if (currentBroughtBack && freshStats[edit.exerciseId]) {
    ExerciseStat.unmarkQuarantined(freshStats[edit.exerciseId]);
  }
  return CoachCarryOutcome.Carried;
}

/**
 * The whole Exercise Settings save onto the fresh documents, in the one safe order: the coach-only
 * parts first (they need the fresh entry exactly as training left it, to see whether it moved),
 * then today's merge. Answers `ChangedMeanwhile` without touching anything when the coach's part
 * must be refused, and undefined when there was no coach edit.
 */
export function applyExerciseSettingsSave(
  freshSubSlot: Plan_SubSlot,
  freshStats: FitnessState['exerciseStats'],
  exerciseId: string,
  before: ExerciseSettingsSummary,
  after: ExerciseSettingsSummary,
  resultSubSlot: Plan_SubSlot | undefined,
  coachEdit: CoachExerciseEdit | undefined,
): CoachCarryOutcome | undefined {
  let outcome: CoachCarryOutcome | undefined;
  if (coachEdit) {
    outcome = carryCoachExerciseChange(freshSubSlot, freshStats, coachEdit);
    if (outcome === CoachCarryOutcome.ChangedMeanwhile) return outcome;
  }
  applyExerciseSettingsChange(freshSubSlot, freshStats, exerciseId, before, after, resultSubSlot);
  return outcome;
}
